import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    InputSelector,
    Typography,
    createOptionMessageResolver,
    TooltipAnchor,
    TOOLTIP_BOTTOM_LOCATION,
    TOOLTIP_MIDDLE_ALIGNMENT,
    FormComputed,
    useForm,
    Colours,
    Question,
    PROVINCE,
    AnchorAnimation,
    QuestionCompact,
    HiddenField,
    BOOLEAN_OPTIONS,
} from '@ratehub/base-ui';
import { PAYMENT_FREQUENCIES } from '@ratehub/documents/mtg-journey-document/stable';
import {
    AMORTIZATION_TERM_MESSAGES,
    AMORTIZATION_OPTIONS_EXTENDED,
    PAYMENT_FREQUENCY_MESSAGES,
    SCENARIO_TYPES,
    DEFAULT_ALL_PURCHASE_PARAMS,
} from '@ratehub/mtg-common';

import getCalculatorGridColumnClassNames from '../functions/getCalculatorGridColumnClassNames';
import isColumnHidden from '../functions/isColumnHidden';

import CalculatorGridStyles from '../styles/CalculatorGridStyles';
import getMonthlyPayment from '../functions/getPaymentCalculatorMonthlyPayment';
import getLandTransferTaxCalculations from '../functions/getLandTransferTaxCalculations';
import DOWNPAYMENT_INSURABILITY_OPTIONS from '../definitions/DownPaymentInsurabilityOptions';
import PaymentCalculatorRateSelector from './PaymentCalculatorRateSelector';
import PaymentCalculatorLeadFormButton from './PaymentCalculatorLeadFormButton';
import CalculationGridRows from './CalculationGridRows';
import PaymentCalculatorScenarioCalculations from './PaymentCalculatorScenarioCalculations';

// Generated columns of scenarios (0-3)
const COLUMN_NUMBERS = [ ...Array(4).keys() ];

const TOOLTIP_WIDTH = '20rem';

const DEFAULT_MORTGAGE_TERM = DEFAULT_ALL_PURCHASE_PARAMS.term;

// Used to supply the correct values based on scenario index for the computed outputs
const MONTHLY_PAYMENT_CALCULATIONS = COLUMN_NUMBERS.map(
    column => values =>
        getMonthlyPayment({
            formValues: values,
            paymentScenario: column,
        }),
);

const FIRST_TIME_BUYER_PROVINCES = [ 'ON', 'PE', 'BC' ];

function PaymentCalculatorScenarioGrid({ scenarioCount, values, preferredProvider, isProductReferralDisabled, ...otherProps }) {
    const form = useForm();
    const intl = useIntl();
    const lines = useMemo(
        () => getCalculationLines(),
        [],
    );

    const { scenarioType, location } = form.values;

    const { scenarios, isFirstTimeBuyer } = values ?? {};
    const isFirstTimeBuyerFieldEnabled = FIRST_TIME_BUYER_PROVINCES.includes(location?.province.code);
    const isNewHomeFieldEnabled = location?.province.code === PROVINCE.BRITISH_COLUMBIA;
    const isTransferFeeNoticeEnabled = !isProductReferralDisabled && (location?.province.code === PROVINCE.ALBERTA || location?.province.code === PROVINCE.SASKATCHEWAN);
    const transferFeeNoticeUrl = location?.province.code === PROVINCE.ALBERTA
        ? '/land-transfer-tax-alberta'
        : '/land-transfer-tax-saskatchewan';

    function getAmortizationOptions(column) {
        const downPaymentPercent = form.values?.scenarios?.[column].downPayment?.percent;

        return AMORTIZATION_OPTIONS_EXTENDED
            .map(term => ({
                value: term,
                label: intl.formatMessage(AMORTIZATION_TERM_MESSAGES.TERM_MESSAGE, { term }),
                disabled: term > 25 && downPaymentPercent < 20,
            }));
    }

    return (
        <Container
            $columnCount={scenarioCount}
            {...otherProps}
        >
            <If condition={scenarioType === SCENARIO_TYPES.PURCHASE}>
                <CalculationGridRows
                    className="downpayment-cmhc-grid"
                    scenarioCount={scenarioCount}
                    values={{
                        downPaymentDollars: scenarios?.map(scenario => scenario?.downPayment?.dollars),
                    }}
                />
            </If>

            <div className="row">
                <div className="col-0">
                    <span className={Typography.CLASSES.TEXT_M}>
                        <FormattedMessage
                            id="mtg-calculator.PaymentCalculatorInputs.amortizationLabel"
                            defaultMessage="Amortization"
                        />
                    </span>

                    <TooltipAnchor
                        className="tooltip"
                        message={(
                            <FormattedMessage
                                id="mtg-calculator.PaymentCalculatorInputs.amortizationTooltip"
                                defaultMessage="The length of time it will take a homeowner to pay off their mortgage.
                                    In Canada, the maximum amortization period for insurable mortgages is 25 years.
                                    Longer amortization periods allow homeowners to make smaller monthly payments,
                                    but equate to more interest paid over the life of the mortgage."
                            />
                        )}
                        tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                        nubbinAlignment={TOOLTIP_MIDDLE_ALIGNMENT}
                        tooltipWidth={TOOLTIP_WIDTH}
                    />
                </div>

                <If condition={isProductReferralDisabled}>
                    <HiddenField
                        name="defaultRateValue"
                    />
                </If>

                <For
                    each="column"
                    of={COLUMN_NUMBERS}
                >
                    <div
                        key={`column-${column}`}
                        className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                    >
                        <InputSelector
                            aria-label={(
                                <FormattedMessage
                                    id="mtg-calculator.PaymentCalculatorInputs.amortizationFieldLabel"
                                    defaultMessage="Amortization"
                                />
                            )}
                            field={{
                                name: `scenarios[${column}].amortization`,
                                type: 'choice',
                                options: getAmortizationOptions(column),
                                value: scenarios?.[column]?.amortization ?? 25,
                                validators: [
                                    {
                                        fn: amortization =>
                                            // If less than 25 years, downPaymentPercent has to be at least 20%
                                            parseInt(amortization) <= 25
                                                ? true
                                                : scenarioType === SCENARIO_TYPES.PURCHASE
                                                    ? form.values?.scenarios?.[column].downPayment?.percent >= 20
                                                    : form.values.downPaymentPercentage === DOWNPAYMENT_INSURABILITY_OPTIONS.MORE,
                                        message: (
                                            <FormattedMessage
                                                id="mtg-common.validators.errorMessage.amortizationAndDownPayment"
                                                defaultMessage="The maximum amortization period is 25 years for mortgages with less than 20% down."
                                            />
                                        ),
                                    },
                                ],
                            }}
                            type="dropdown"
                        />
                    </div>
                </For>
            </div>

            <div className="row">
                <div className="col-0">
                    <span className={Typography.CLASSES.TEXT_M}>
                        <FormattedMessage
                            id="mtg-calculator.PaymentCalculatorInputs.mortgageRateLabel"
                            defaultMessage="Mortgage rate"
                        />
                    </span>

                    <TooltipAnchor
                        className="tooltip"
                        message={(
                            <Choose>
                                <When condition={isProductReferralDisabled}>
                                    <FormattedMessage
                                        id="mtg-calculator.PaymentCalculatorInputs.mortgageRateTooltip-no-referral"
                                        defaultMessage={'The rate of interest you will pay on the outstanding balance of your mortgage. This is determined by the mortgage type and mortgage provider.'}
                                    />
                                </When>
                                <Otherwise>
                                    <FormattedMessage
                                        id="mtg-calculator.PaymentCalculatorInputs.mortgageRateTooltip"
                                        defaultMessage={'The rate of interest you will pay on the outstanding balance of your mortgage. This is determined by the mortgage type and mortgage provider. To see how rates vary by type and provider, click on "compare".'}
                                    />
                                </Otherwise>
                            </Choose>

                        )}
                        tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                        nubbinAlignment={TOOLTIP_MIDDLE_ALIGNMENT}
                        tooltipWidth={TOOLTIP_WIDTH}
                    />
                </div>

                <For
                    each="column"
                    of={COLUMN_NUMBERS}
                >
                    <Choose>
                        <When condition={isProductReferralDisabled}>
                            <If condition={!isColumnHidden(column, scenarioCount)}>
                                <FormComputed
                                    name={`scenarios[${column}].selectedRate`}
                                    value={(_, formObject) => {
                                        const rateValue = formObject.getValue(`scenarios[${column}].rateValue`);

                                        return rateValue
                                            ? {
                                                value: rateValue,
                                                // assume 5yr term, this is REQUIRED for Interest Rate Risk and Amortization Schedule accordions
                                                term: DEFAULT_MORTGAGE_TERM,
                                            }
                                            : null;
                                    }}
                                    dependencies={[]}
                                />

                                <QuestionCompact
                                    {...otherProps}
                                    field={{
                                        name: `scenarios[${column}].rateValue`,
                                        type: 'float',
                                        formula: formValues => formValues.defaultRateValue?.toString(),
                                    }}
                                    type="percent"
                                    labelMessage={(
                                        <FormattedMessage
                                            id="mtg-calculator.payment-calculator.rate-selector.rateValue.label"
                                            defaultMessage="Rate (%)"
                                        />
                                    )}
                                    // requirement: update the other rate value fields to be non-empty when the user changes the rate value
                                    onChange={() => {
                                        const rateValue = form.getValue(`scenarios[${column}].rateValue`);
                                        const otherRateValueFields = form.fields.filter(field => field.name.endsWith('.rateValue') && field.name !== `scenarios[${column}].rateValue`);

                                        if (otherRateValueFields.every(field => field.isValueComputed)) {
                                            form.setValue('defaultRateValue', rateValue);
                                        }
                                    }}
                                />
                            </If>
                        </When>
                        <Otherwise>
                            <PaymentCalculatorRateSelector
                                key={`column-${column}`}
                                className={classNames('rh-box-sizing-border-box rh-height-100p', getCalculatorGridColumnClassNames(column, scenarioCount))}
                                aria-hidden={column >= scenarioCount}
                                column={column}
                                preferredProvider={preferredProvider}
                            />
                        </Otherwise>
                    </Choose>

                </For>
            </div>

            <div className="row">
                <span className={classNames('col-0', Typography.CLASSES.TEXT_M)}>
                    <FormattedMessage
                        id="mtg-calculator.PaymentCalculatorInputs.paymentFrequencyLabel"
                        defaultMessage="Payment frequency"
                    />
                </span>

                <For
                    each="column"
                    of={COLUMN_NUMBERS}
                >
                    <div
                        key={`column-${column}`}
                        className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                        aria-hidden={isColumnHidden(column, scenarioCount)}
                    >
                        <InputSelector
                            aria-label={(
                                <FormattedMessage
                                    id="mtg-calculator.PaymentCalculatorInputs.paymentFrequencyFieldLabel"
                                    defaultMessage="Payment frequency"
                                />
                            )}
                            field={{
                                name: `scenarios[${column}].paymentFrequency`,
                                type: 'choice',
                                options: Object.values(PAYMENT_FREQUENCIES),
                                value: scenarios?.[column]?.paymentFrequency ?? PAYMENT_FREQUENCIES.MONTHLY,
                            }}
                            type="dropdown"
                            renderOptionLabel={createOptionMessageResolver(
                                intl,
                                Object.values(PAYMENT_FREQUENCY_MESSAGES),
                                'Mortgages.paymentFrequencies.',
                            )}
                        />
                    </div>
                </For>
            </div>

            <div className="row row--blue-light">
                <div className="col-0">
                    <span className="operator blue-dark rh-text-m weight-medium">
                        {'='}
                    </span>
                    <span className="blue-dark rh-text-m weight-medium">
                        <FormattedMessage
                            id="mtg-calculator.PaymentCalculatorInputs.mortgagePaymentLabel"
                            defaultMessage="Mortgage payment"
                        />
                    </span>

                    <TooltipAnchor
                        className="tooltip"
                        message={(
                            <FormattedMessage
                                id="mtg-calculator.PaymentCalculatorInputs.mortgagePaymentTooltip"
                                defaultMessage="The monthly mortgage payment is calculated based on the inputs you
                                    provided: the mortgage amount, rate type (fixed or variable), rate term,
                                    amortization period, and payment frequency. A general affordability rule, as
                                    outlined by the Canada Mortgage and Housing Corporation, is that your monthly
                                    housing costs should not exceed 32% of your gross household monthly income."
                            />
                        )}
                        tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                        nubbinAlignment={TOOLTIP_MIDDLE_ALIGNMENT}
                        tooltipWidth={TOOLTIP_WIDTH}
                    />
                </div>
                <For
                    each="column"
                    of={COLUMN_NUMBERS}
                >
                    <div
                        key={`column-${column}`}
                        className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                        aria-hidden={isColumnHidden(column, scenarioCount)}
                    >
                        {/* Some of our formulas need access to the current scenario.
                            This makes it easier to work with.
                        */}
                        <FormComputed
                            className="blue-dark rh-text-m weight-medium rh-ml-1"
                            data-name={`mortgagePayment-col-${column}`}
                            name={`scenarios[${column}].mortgagePayment`}
                            value={MONTHLY_PAYMENT_CALCULATIONS[column]}
                            format="CAD0"
                            hidden={false}
                        />
                    </div>
                </For>
            </div>

            <If condition={form.values.scenarios}>
                <If condition={!isProductReferralDisabled}>
                    <div className="row">
                        <For
                            each="column"
                            of={COLUMN_NUMBERS}
                        >
                            <PaymentCalculatorLeadFormButton
                                key={`column-${column}`}
                                className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                                column={column}
                                selectedRate={form.values.scenarios[column].selectedRate}
                                rateFetchingOptions={{
                                    amortization: parseInt(form.values.scenarios[column]?.amortization),
                                    term: form.values.scenarios[column]?.rateTerm,
                                    type: form.values.scenarios[column]?.rateType,
                                    city: form.values?.location,
                                    scenario: form.values?.scenarioType,
                                }}
                                aria-hidden={column >= scenarioCount}
                            />
                        </For>
                    </div>
                </If>

                <If condition={scenarioType === SCENARIO_TYPES.PURCHASE}>
                    <PaymentCalculatorScenarioCalculations
                        className="land-transfer-tax rh-pt-1 rh-mx-1"
                        lines={lines}
                        data-test-name="land-transfer-tax"
                    >
                        <If condition={isFirstTimeBuyerFieldEnabled}>
                            <Question
                                className="no-margin"
                                field={{
                                    name: 'isFirstTimeBuyer',
                                    type: 'boolean',
                                    value: !!isFirstTimeBuyer,
                                    options: BOOLEAN_OPTIONS,
                                }}
                                type="radio"
                                labelMessage={(
                                    <FormattedMessage
                                        id="calculators.PaymentCalculatorScenarioCalculations.firstTimeBuyerLabel"
                                        defaultMessage="Are you a first time home buyer?"
                                    />
                                )}
                                flowDirection="row"
                            />
                        </If>
                        <If condition={isNewHomeFieldEnabled}>
                            <Question
                                className="no-margin rh-pt-0_5"
                                field={{
                                    name: 'isNewlyBuiltHome',
                                    type: 'boolean',
                                    value: false,
                                    options: BOOLEAN_OPTIONS,
                                }}
                                type="radio"
                                labelMessage={(
                                    <FormattedMessage
                                        id="calculators.PaymentCalculatorScenarioCalculations.newlyBuiltHomeLabel"
                                        defaultMessage="Are you buying a newly built home?"
                                    />
                                )}
                                flowDirection="row"
                            />
                        </If>
                        <If condition={isTransferFeeNoticeEnabled}>
                            <p className="rh-mt-0">
                                <FormattedMessage
                                    id="calculators.PaymentCalculatorScenarioCalculations.transferFeeLabel"
                                    defaultMessage="This province does not have a Land Transfer Tax. You may be required to pay <transferFeeLink>title transfer fees.</transferFeeLink>"
                                    values={{
                                        transferFeeLink: chunks => (
                                            <a
                                                href={transferFeeNoticeUrl}
                                                target="_blank"
                                            >
                                                {chunks}
                                            </a>
                                        ),
                                    }}
                                />
                            </p>
                        </If>
                    </PaymentCalculatorScenarioCalculations>
                </If>
            </If>
        </Container>
    );
}

PaymentCalculatorScenarioGrid.propTypes = {
    scenarioCount: PropTypes.number.isRequired,
    values: PropTypes.shape({
        scenarios: PropTypes.arrayOf(PropTypes.shape({
            rateType: PropTypes.string,
            rateTerm: PropTypes.number,
            amortization: PropTypes.number,
            downPayment: PropTypes.number,
            paymentFrequency: PropTypes.string,
        })),
        isFirstTimeBuyer: PropTypes.bool,
    }),
    preferredProvider: PropTypes.string,
    isProductReferralDisabled: PropTypes.bool,
};

PaymentCalculatorScenarioGrid.defaultProps = {
    values: undefined,
    preferredProvider: undefined,
    isProductReferralDisabled: undefined,
};

function getCalculationLines() {
    return [
        {
            id: 'provincialTax',
            name: 'provincialTax',
            value: (values) => getLandTransferTaxCalculations({
                homePrice: values.askingPrice,
                homeLocation: values.location,
            }, 'provincialTax'),
            label: (
                <FormattedMessage
                    id="mtg-calculator.PaymentCalculatorInputs.provincialTaxLabel"
                    defaultMessage="Provincial"
                />
            ),
            isSubLine: true,
        },
        {
            id: 'municipalTax',
            name: 'municipalTax',
            value: (values) => getLandTransferTaxCalculations({
                homePrice: values.askingPrice,
                homeLocation: values.location,
            }, 'municipalTax'),
            label: (
                <FormattedMessage
                    id="mtg-calculator.PaymentCalculatorInputs.municipalTaxLabel"
                    defaultMessage="Municipal"
                />
            ),
            isSubLine: true,
            operator: 'plus',
        },
        {
            id: 'lttRebate',
            name: 'lttRebate',
            value: (values) => getLandTransferTaxCalculations({
                homePrice: values.askingPrice,
                homeLocation: values.location,
                isFirstTimeBuyer: values.isFirstTimeBuyer,
                isNewlyBuiltHome: values.isNewlyBuiltHome,
            }, 'rebate'),
            label: (
                <FormattedMessage
                    id="mtg-calculator.PaymentCalculatorInputs.lttRebateLabel"
                    defaultMessage="Rebate"
                />
            ),
            isSubLine: true,
            operator: 'minus',
        },
        {
            id: 'landTransferTax',
            name: 'landTransferTax',
            value: (values) => getLandTransferTaxCalculations({
                homePrice: values.askingPrice,
                homeLocation: values.location,
                isFirstTimeBuyer: values.isFirstTimeBuyer,
                isNewlyBuiltHome: values.isNewlyBuiltHome,
            }, 'landTransferTax'),
            label: (
                <FormattedMessage
                    id="mtg-calculator.PaymentCalculatorInputs.landTransferTaxLabel"
                    defaultMessage="Land transfer tax"
                />
            ),
        },
    ];
}

const Container = styled.section`
    ${CalculatorGridStyles};

    > .downpayment-cmhc-grid {
        ${CalculatorGridStyles};
    }

    > .land-transfer-tax {
        border-top: 1px solid ${Colours.STONE_DARK};

        a {
            ${AnchorAnimation};
        }

        .no-margin {
            margin: 0;
        }
    }
`;

export default observer(PaymentCalculatorScenarioGrid);
